import { useCallback, useEffect, useRef, useState } from "react";
import { Button, TextField } from "@legends/ui";
import {
  deleteBoardSlate,
  listBoardSlates,
  uploadBoardSlate,
  type BoardSlate,
} from "./api";
import { invalidateBoardSlates } from "./boardSlates";
import { Section, alertStyle, errMsg, monoLabelStyle, rowStyle } from "./sections";
import { useListControls } from "./listControls";

// Board slates admin (ADR 0058). The super-admin owns the GLOBAL catalog of custom
// whiteboard board backgrounds: image slates that appear in every board picker
// alongside the built-in vector courts (ADR 0045/0057). Uploads reuse the ad/roll
// /uploads image pipeline. Deleting a slate never rewrites the built-in boards;
// program state that referenced it simply falls back to the blank vector board.

export default function BoardSlatesAdminScreen() {
  const [slates, setSlates] = useState<BoardSlate[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(() => {
    listBoardSlates()
      .then(setSlates)
      .catch((err: unknown) => setError(errMsg(err)));
  }, []);

  useEffect(() => {
    setError(null);
    load();
  }, [load]);

  // Any mutation invalidates the shared public catalog cache so the next
  // whiteboard render site (Director/Legend/guest/Fan) refetches (boardSlates.tsx).
  const guard = (p: Promise<unknown>) => {
    setBusy(true);
    p.then(() => {
      invalidateBoardSlates();
      load();
    })
      .catch((err: unknown) => setError(errMsg(err)))
      .finally(() => setBusy(false));
  };

  const upload = (file: File) => {
    guard(uploadBoardSlate(file, label.trim()));
    setLabel("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const remove = (s: BoardSlate) => guard(deleteBoardSlate(s.id));

  const { toolbar, result } = useListControls(slates, {
    searchText: (s) => [s.label],
    searchPlaceholder: "Search slates…",
    sorts: [{ key: "name", label: "Name (A–Z)", cmp: (a, b) => (a.label || "").localeCompare(b.label || "") }],
  });

  return (
    <div style={{ maxWidth: 820, display: "grid", gap: 16 }}>
      <Section title="Board slates">
        <p style={{ margin: "8px 0 12px", fontSize: "var(--fs-xs)", color: "var(--text-dim)" }}>
          Upload custom whiteboard board backgrounds (image slates). They appear in
          every board picker alongside the built-in courts — the Director sets the
          board, the Legend telestrates on it.
        </p>

        {toolbar}
        <div style={{ marginTop: 4 }}>
          {result.map((s) => (
            <div key={s.id} style={rowStyle}>
              <img
                src={s.url}
                alt=""
                aria-hidden
                style={{ width: 64, height: 36, objectFit: "cover", borderRadius: "var(--radius-sm)", border: "1px solid var(--border)", background: "#000" }}
              />
              <span style={{ fontSize: "var(--fs-body-2)", color: "var(--text-hi)" }}>
                {s.label || "(untitled)"}
              </span>
              <div style={{ marginLeft: "auto" }}>
                <Button
                  size="sm"
                  variant="muted"
                  aria-label={`Delete ${s.label || "board slate"}`}
                  disabled={busy}
                  onClick={() => remove(s)}
                >
                  Delete
                </Button>
              </div>
            </div>
          ))}
          {result.length === 0 && (
            <p style={{ margin: 0, fontSize: "var(--fs-xs)", color: "var(--text-dim)" }}>
              {slates.length === 0
                ? "No custom board slates yet — the built-in vector courts still work."
                : "No slates match your search."}
            </p>
          )}
        </div>

        <form
          aria-label="Add board slate"
          onSubmit={(e) => e.preventDefault()}
          style={{
            marginTop: 14,
            paddingTop: 12,
            borderTop: "1px solid var(--border-subtle)",
            display: "flex",
            alignItems: "flex-end",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <TextField label="Label" name="slate-label" value={label} onChange={setLabel} />
          <label style={{ ...monoLabelStyle, cursor: busy ? "default" : "pointer", color: "var(--accent)" }}>
            {busy ? "Uploading…" : "＋ Upload slate image"}
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              aria-label="Upload board slate image"
              disabled={busy}
              style={{ display: "none" }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) upload(f);
              }}
            />
          </label>
        </form>
      </Section>

      {error && (
        <p role="alert" style={alertStyle}>
          {error}
        </p>
      )}
    </div>
  );
}
